// Per-encounter "why does this patient owe $X?" rows (GDN-2400, Option B).
//
// Renders the financial summary's `encounters` payload — previously fetched
// but unrendered in the PatientResponsibilityPanel — as one row per visit:
// service date, type/provider, charged, insurance paid, patient owes (with
// PR-reason chips when the ERA breakdown is present), collected, and a net
// status chip, plus a plain-English explanation. Follows the visual language
// of the approved `prototypes/option-b/PaymentsWhyTab` prototype.
//
// Degrades gracefully: every field is optional on the generated
// EncounterLedgerGroup — rows render only what the API actually carries.

import React from 'react';
// Direct file import (no feature barrel) — shared eligibility design tokens.
import { ELIG_TOKENS } from '@features/eligibility-checks/theme/readinessTokens';
import { Box, Chip, Typography } from '@mui/material';

import type {
  EncounterLedgerGroup,
  PatientResponsibilityBreakdown,
} from '../store';

import { formatCents } from './v2/shared';

interface EncounterWhyRowsProps {
  encounters: EncounterLedgerGroup[];
}

export const EncounterWhyRows: React.FC<EncounterWhyRowsProps> = ({ encounters }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
    {encounters.map((encounter, index) => (
      <EncounterWhyRow
        key={encounter.encounter_id ?? `encounter-${index}`}
        encounter={encounter}
      />
    ))}
  </Box>
);

// ─── Per-encounter row ───────────────────────────────────────────────────────

const EncounterWhyRow = ({ encounter }: { encounter: EncounterLedgerGroup }) => {
  const net = netStatus(encounter);
  const breakdown = breakdownItems(encounter.patient_responsibility_breakdown);
  const why = whySentence(encounter);

  return (
    <Box
      sx={{
        border: `1px solid ${ELIG_TOKENS.lineStrong}`,
        borderRadius: 1,
        bgcolor: ELIG_TOKENS.card,
        p: 1.5,
      }}
    >
      {/* Header line: date · type · provider ─ net status */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
          mb: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            {encounter.service_date && (
              <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 700 }}>
                {shortDate(encounter.service_date)}
              </Typography>
            )}
            {encounter.encounter_type_label && (
              <Typography variant="body2" sx={{ color: ELIG_TOKENS.inkSoft }}>
                {encounter.encounter_type_label}
              </Typography>
            )}
          </Box>
          {(encounter.provider_name || encounter.primary_payer_name) && (
            <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
              {[encounter.provider_name, encounter.primary_payer_name]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}
        </Box>
        <Chip
          size="small"
          label={net.label}
          sx={{
            flexShrink: 0,
            fontWeight: 700,
            color: net.color,
            bgcolor: net.bg,
          }}
        />
      </Box>

      {/* Money columns: charged / insurance paid / patient owes / collected */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 1,
          mb: breakdown.length > 0 || why ? 1 : 0,
        }}
      >
        <MoneyCell
          label="Charged"
          value={encounter.charged_cents != null ? formatCents(encounter.charged_cents) : '—'}
        />
        <MoneyCell
          label="Insurance paid"
          value={
            encounter.insurance_paid_cents != null
              ? formatCents(encounter.insurance_paid_cents)
              : '—'
          }
          sub={pendingClaimLabel(encounter)}
        />
        <MoneyCell
          label="Patient owes"
          value={
            encounter.patient_due_cents != null
              ? formatCents(encounter.patient_due_cents)
              : 'TBD'
          }
          sub={
            encounter.patient_due_cents == null && encounter.claim
              ? 'awaiting ERA'
              : undefined
          }
        />
        <MoneyCell
          label="Collected"
          value={formatCents(encounter.patient_paid_cents ?? 0)}
        />
      </Box>

      {/* ERA PR-reason chips — why the plan says the patient owes this */}
      {breakdown.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: why ? 1 : 0 }}>
          {breakdown.map(part => (
            <Chip
              key={part.kind}
              size="small"
              label={`${part.label} ${formatCents(part.amountCents)}`}
              sx={{
                height: 18,
                fontSize: 11,
                fontWeight: 600,
                color: part.color,
                bgcolor: part.bg,
              }}
            />
          ))}
        </Box>
      )}

      {/* Plain-English "why" sentence */}
      {why && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: ELIG_TOKENS.inkSoft,
            bgcolor: ELIG_TOKENS.bg,
            borderRadius: 0.5,
            px: 1,
            py: 0.75,
          }}
        >
          {why}
        </Typography>
      )}
    </Box>
  );
};

const MoneyCell = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <Box>
    <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint, display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: ELIG_TOKENS.ink, fontWeight: 700 }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" sx={{ color: ELIG_TOKENS.inkFaint }}>
        {sub}
      </Typography>
    )}
  </Box>
);

// ─── Derivations ─────────────────────────────────────────────────────────────

const owedCents = (encounter: EncounterLedgerGroup): number | null => {
  if (encounter.patient_balance_cents != null) return encounter.patient_balance_cents;
  if (encounter.patient_due_cents == null) return null;
  return encounter.patient_due_cents - (encounter.patient_paid_cents ?? 0);
};

const netStatus = (
  encounter: EncounterLedgerGroup,
): { label: string; color: string; bg: string } => {
  if (encounter.patient_due_cents == null) {
    const label =
      encounter.claim?.status_label ??
      (encounter.claim ? 'Claim pending' : 'Pending');
    return { label, color: ELIG_TOKENS.gray, bg: ELIG_TOKENS.graySoft };
  }
  const owed = owedCents(encounter) ?? 0;
  if (owed > 0) {
    return {
      label: `Owes ${formatCents(owed)}`,
      color: ELIG_TOKENS.amber,
      bg: ELIG_TOKENS.amberSoft,
    };
  }
  if (owed < 0) {
    return {
      label: `Credit ${formatCents(-owed)}`,
      color: ELIG_TOKENS.green,
      bg: ELIG_TOKENS.greenSoft,
    };
  }
  return { label: 'Settled', color: ELIG_TOKENS.green, bg: ELIG_TOKENS.greenSoft };
};

const pendingClaimLabel = (encounter: EncounterLedgerGroup): string | undefined => {
  if (!encounter.claim) return undefined;
  if (encounter.patient_due_cents != null) return undefined;
  return 'claim pending';
};

interface BreakdownChip {
  kind: string;
  label: string;
  amountCents: number;
  color: string;
  bg: string;
}

/**
 * PR-reason chips from the adjudicated responsibility split. PR group codes
 * (PR-1 deductible, PR-2 coinsurance, PR-3 copay) are the standard CARC
 * patient-responsibility reasons the ERA carries.
 */
const breakdownItems = (
  breakdown: PatientResponsibilityBreakdown | null | undefined,
): BreakdownChip[] => {
  if (!breakdown) return [];
  return [
    {
      kind: 'copay',
      label: 'Copay (PR-3)',
      amountCents: breakdown.copay_cents ?? 0,
      color: ELIG_TOKENS.green,
      bg: ELIG_TOKENS.greenSoft,
    },
    {
      kind: 'deductible',
      label: 'Deductible (PR-1)',
      amountCents: breakdown.deductible_cents ?? 0,
      color: ELIG_TOKENS.amber,
      bg: ELIG_TOKENS.amberSoft,
    },
    {
      kind: 'coinsurance',
      label: 'Coinsurance (PR-2)',
      amountCents: breakdown.coinsurance_cents ?? 0,
      color: ELIG_TOKENS.midnight,
      bg: 'rgba(28,30,133,0.08)',
    },
    {
      kind: 'other',
      label: 'Other',
      amountCents: breakdown.other_cents ?? 0,
      color: ELIG_TOKENS.gray,
      bg: ELIG_TOKENS.graySoft,
    },
  ].filter(part => part.amountCents > 0);
};

const whySentence = (encounter: EncounterLedgerGroup): string | null => {
  const collected = encounter.patient_paid_cents ?? 0;
  if (encounter.patient_due_cents == null) {
    if (!encounter.claim) return null;
    const charged =
      encounter.charged_cents != null ? ` for ${formatCents(encounter.charged_cents)}` : '';
    const collectedNote =
      collected > 0
        ? ` ${formatCents(collected)} collected at check-in will apply once the ERA lands.`
        : ' Patient responsibility is set when the ERA lands.';
    return `Claim${charged} is still with the payer.${collectedNote}`;
  }

  const reasons = breakdownItems(encounter.patient_responsibility_breakdown)
    .map(part => `${formatCents(part.amountCents)} ${part.kind}`)
    .join(' + ');
  const paid =
    encounter.insurance_paid_cents != null
      ? `Insurance paid ${formatCents(encounter.insurance_paid_cents)}`
      : 'Insurance paid —';
  const of =
    encounter.charged_cents != null ? ` of ${formatCents(encounter.charged_cents)}` : '';
  const base = `${paid}${of}; the plan set ${formatCents(encounter.patient_due_cents)} patient responsibility${reasons ? ` (${reasons})` : ''}.`;
  const owed = owedCents(encounter) ?? 0;
  if (owed > 0) {
    return `${base} Collected ${formatCents(collected)} so far → ${formatCents(owed)} still owed.`;
  }
  if (owed < 0) {
    return `${base} Collected ${formatCents(collected)} → ${formatCents(-owed)} sits as credit.`;
  }
  return `${base} Collected ${formatCents(collected)} → settled.`;
};

const shortDate = (date: string): string => {
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};
